import { type CURRENCY, type CurrencyEnumBaseData, type HexAlpha, hexAlpha, isDefined } from '@nfctron/nexus-core';

import { currencyEnumTickets } from '@root/lib/constants/currency';
import { type LANGUAGE, languageEnumTickets } from '@root/lib/constants/language';

/**
 * Event currency type
 * @export
 */
export type EventCurrency = {
	baseCurrency: CurrencyEnumBaseData;
	virtualCurrency?: EventVirtualCurrency;
};

/**
 * Event virtual currency type
 * @export
 */
export type EventVirtualCurrency = {
	rate: number;
	name: string;
	texts: Partial<Record<LANGUAGE, { few: string; many: string; one: string; other: string }>>;
};

/**
 * Event color config type
 * @export
 */
export type EventColorConfig<C = HexAlpha> = {
	background: C;
	name: C;
	text: C;
	link: C;
	subtitle: C;
};

/**
 * Constructs an event color config object
 * @param {Input} input
 * @returns {EventColorConfig}
 */
export const constructEventColorConfig = <Input extends ConstructEventColorConfigInput = ConstructEventColorConfigInput>(
	input: Input,
): EventColorConfig => ({
	background: hexAlpha(input.background),
	name: hexAlpha(input.name),
	text: hexAlpha(input.text),
	link: hexAlpha(input.link),
	subtitle: hexAlpha(input.subtitle),
});

/**
 * Input type for event color config constructor
 * @export
 */
export type ConstructEventColorConfigInput = {
	background: string;
	name: string;
	text: string;
	highlight: string;
	link: string;
	subtitle: string;
};

/**
 * Constructs an event virtual currency object
 * @param {Input} input
 * @returns {EventVirtualCurrency}
 * @export
 */
export const constructEventVirtualCurrency = <Input extends ConstructEventVirtualCurrencyInput = ConstructEventVirtualCurrencyInput>(
	input: Input,
): EventVirtualCurrency => ({
	name: input.name,
	rate: input.rate,
	texts: Object.entries(input.texts).reduce(
		(acc, [lang, texts]) => ({
			...acc,
			[languageEnumTickets.get(Number(lang)).languageId]: texts,
		}),
		{} as EventVirtualCurrency['texts'],
	),
});

/**
 * Input type for event virtual currency constructor
 * @export
 */
export type ConstructEventVirtualCurrencyInput = {
	texts: Record<
		string,
		{
			few: string;
			many: string;
			one: string;
			other: string;
		}
	>;
	rate: number;
	name: string;
};

/**
 * Constructs an event currency object
 * @param {Input} input
 * @returns {EventCurrency}
 * @export
 */
export const constructEventCurrency = <Input extends ConstructEventCurrencyInput = ConstructEventCurrencyInput>(input: Input): EventCurrency => ({
	baseCurrency: currencyEnumTickets.get(input.baseCurrencyId),
	virtualCurrency: isDefined(input.virtualCurrency) ? constructEventVirtualCurrency(input.virtualCurrency) : undefined,
});

/**
 * Input type for event currency constructor
 * @export
 */
export type ConstructEventCurrencyInput = {
	baseCurrencyId: CURRENCY;
	virtualCurrency?: ConstructEventVirtualCurrencyInput;
};
